import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, Rotate3d } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUILDINGS } from "@/data/content";
import { ImagePlaceholder } from "@/components/site/ImagePlaceholder";
import { MaskLine, Reveal } from "@/components/site/Reveal";

const photoLabel = (b, kind, i) =>
  `${b.slug.replace("bungalow-", "").replace("maison-principale", "maison").toUpperCase()}-${kind}-0${i + 1}`;

const Gallery = ({ title, building, kind, count, images, feature = false }) => {
  const feat = feature || (count % 2 === 1 && count > 1);
  const uniform = !feat && count >= 6;
  const restInThree = (count - 1) % 3 === 0;
  return (
  <div className="mt-16 md:mt-24">
    <Reveal>
      <h2 className="flex items-baseline gap-4 font-serif text-3xl tracking-tight text-ink md:text-4xl">
        {title}
        <span className="text-sm italic text-ink/30">{String(count).padStart(2, "0")}</span>
      </h2>
    </Reveal>
    <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-12 md:gap-5">
      {Array.from({ length: count }).map((_, i) => {
        const wide = i === 0 && !uniform;
        const ratio = wide
          ? feat
            ? "aspect-[16/10] md:aspect-[16/8]"
            : "aspect-[16/9]"
          : "aspect-[4/3]";
        const label = photoLabel(building, kind, i);
        const raw = images && images[i];
        const src = raw && typeof raw === "object" ? raw.src : raw;
        const pos = raw && typeof raw === "object" ? raw.pos : undefined;
        return (
          <Reveal
            key={i}
            delay={0.06 * i}
            className={
              uniform
                ? "col-span-1 md:col-span-6"
                : wide
                  ? feat
                    ? "col-span-2 md:col-span-12"
                    : "col-span-2 md:col-span-7"
                  : feat
                    ? restInThree
                      ? "col-span-2 md:col-span-4"
                      : "col-span-1 md:col-span-6"
                    : i === 1
                      ? "md:col-span-5"
                      : "col-span-1 md:col-span-6"
            }
          >
            {src ? (
              <div className={`relative w-full overflow-hidden border border-neutral-200 ${ratio}`}>
                <img
                  src={src}
                  alt={`${building.name.fr} — ${kind} 0${i + 1}`}
                  data-testid={`img-${label.toLowerCase()}`}
                  loading="lazy"
                  style={pos ? { objectPosition: pos } : undefined}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <ImagePlaceholder label={label} ratio={ratio} />
            )}
          </Reveal>
        );
      })}
    </div>
  </div>
  );
};

export default function BuildingPage() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const index = BUILDINGS.findIndex((b) => b.slug === slug);

  if (index === -1) return <Navigate to="/carte" replace />;

  const b = BUILDINGS[index];
  const prev = BUILDINGS[(index - 1 + BUILDINGS.length) % BUILDINGS.length];
  const next = BUILDINGS[(index + 1) % BUILDINGS.length];

  const localized = (v) =>
    v && typeof v === "object" ? v[lang] : v || t.building.toComplete;

  const specs = [
    ...(!b.noSurface
      ? [{ label: t.building.surface, value: localized(b.surface), id: "surface" }]
      : []),
    ...(!b.noCapacity
      ? [{ label: t.building.capacity, value: localized(b.capacity), id: "capacity" }]
      : []),
    { label: t.building.description, value: b.description[lang], id: "description" },
  ];

  return (
    <div data-testid={`building-page-${b.slug}`} className="pt-28 md:pt-40">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <Link
            to="/carte"
            data-testid="building-back-to-map"
            className="group inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/40 transition-colors duration-300 hover:text-lagoon"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
            {t.building.backToMap}
          </Link>
        </Reveal>

        <p className="mt-10 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-lagoon">
          <span className="h-px w-10 bg-lagoon" />
          {t.building.overline} {b.num} / {String(BUILDINGS.length).padStart(2, "0")}
        </p>
        <div className="mt-4 flex items-end justify-between gap-8">
          {b.logo && (
            <motion.img
              src={b.logo}
              alt={`Logo ${b.name[lang]}`}
              data-testid="building-logo"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="w-20 shrink-0 sm:w-36 md:w-48"
            />
          )}
          <h1 className="whitespace-nowrap text-right font-serif text-[clamp(2rem,9vw,7.5rem)] leading-[0.95] tracking-tight text-ink">
            <MaskLine delay={0.1}>{b.name[lang]}</MaskLine>
          </h1>
        </div>

        <div className={`mt-14 grid border-y border-ink/10 md:mt-20 ${specs.length === 3 ? "md:grid-cols-3" : specs.length === 2 ? "md:grid-cols-2" : ""}`}>
          {specs.map((s, i) => (
            <Reveal
              key={s.id}
              delay={0.08 * i}
              className={`py-8 md:py-10 ${i > 0 ? "border-t border-ink/10 md:border-l md:border-t-0 md:pl-10" : "md:pr-10"}`}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/40">
                {s.label}
              </p>
              <p
                data-testid={`building-${s.id}`}
                className={`mt-3 leading-relaxed ${
                  s.id === "description"
                    ? "text-sm text-ink/60 md:text-base"
                    : "font-serif text-2xl text-ink md:text-3xl"
                }`}
              >
                {s.value}
              </p>
            </Reveal>
          ))}
        </div>

        {b.galleryImages ? (
          <Gallery
            title={t.building.photos}
            building={b}
            kind="PHOTO"
            count={b.galleryImages.length}
            images={b.galleryImages}
            feature
          />
        ) : (
          <>
            <Gallery
              title={t.building.exterior}
              building={b}
              kind={t.building.extLabel}
              count={b.extPhotos}
              images={b.extImages}
              feature={b.featureGallery}
            />
            <Gallery
              title={t.building.interior}
              building={b}
              kind={t.building.intLabel}
              count={b.intPhotos}
              images={b.intImages}
              feature={b.featureGallery}
            />
          </>
        )}

        {!b.noVirtualTour && (
        <Reveal className="mt-16 md:mt-24">
          <div
            data-testid="virtual-tour-section"
            className="border border-ink/10 bg-sand p-8 md:p-14"
          >
            <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="flex items-center gap-3 font-serif text-3xl tracking-tight text-ink md:text-4xl">
                  <Rotate3d className="h-7 w-7 text-lagoon" strokeWidth={1.5} />
                  {t.building.virtualTour}
                </p>
                {!b.virtualTourUrl && (
                  <p className="mt-3 text-sm text-ink/50">
                    {t.building.virtualTourEmpty}
                    <span className="mt-1 block font-mono text-xs text-ink/35">
                      {t.building.virtualTourHint}
                    </span>
                  </p>
                )}
              </div>
              {b.virtualTourUrl ? (
                <a
                  data-testid="virtual-tour-link"
                  href={b.virtualTourUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 border border-ink bg-white px-7 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
                >
                  {t.building.openTour}
                  <ExternalLink className="h-4 w-4" />
                </a>
              ) : (
                <span
                  data-testid="virtual-tour-placeholder"
                  className="inline-flex cursor-not-allowed items-center gap-3 border border-dashed border-ink/20 px-7 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink/30"
                >
                  {t.building.virtualTourEmpty}
                </span>
              )}
            </div>
            {b.virtualTourUrl && (
              <div className="mt-8 overflow-hidden border border-ink/10 bg-white">
                <iframe
                  title={`${t.building.virtualTour} — ${b.name[lang]}`}
                  data-testid="virtual-tour-iframe"
                  src={b.virtualTourUrl}
                  className="aspect-video w-full"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; fullscreen; web-share; xr-spatial-tracking"
                  loading="lazy"
                />
              </div>
            )}
          </div>
        </Reveal>
        )}

        <div className="mt-16 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 md:mt-24">
          {[
            { b: prev, label: t.building.prev, dir: "left" },
            { b: next, label: t.building.next, dir: "right" },
          ].map(({ b: target, label, dir }) => (
            <Link
              key={target.slug}
              to={`/batiment/${target.slug}`}
              data-testid={`building-nav-${dir}`}
              className="group bg-white p-8 transition-colors duration-300 hover:bg-sand md:p-12"
            >
              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/40">
                {dir === "left" && <ArrowLeft className="h-3.5 w-3.5" />}
                {label}
                {dir === "right" && <ArrowRight className="h-3.5 w-3.5" />}
              </p>
              <p className="mt-3 font-serif text-2xl tracking-tight text-ink transition-transform duration-500 group-hover:translate-x-1 md:text-4xl">
                {target.name[lang]}
              </p>
            </Link>
          ))}
        </div>
      </div>
      <div className="h-24 md:h-32" />
    </div>
  );
}
